import { Button } from '@/components/ui/button';
import { ArrowIcon, GoogleIcon, StarIcon } from '@/components/ui/icons';
import { Tag } from '@/components/ui/tag';
import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { useRef } from 'react';

// ─── Données proof of trust ───────────────────────────────────────────────────
const GOOGLE_DATA = {
  score: 4.9,
  count: 91,
  mapsUrl:
    'https://www.google.com/search?sa=X&sca_esv=b81e1b9584c528ab&sxsrf=ANbL-n6nZrQ7EBUcH_PwIeKZDwU33OtkKQ:1776425253751&q=zLawyer+(Zele+Solutions)+Avis&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2MjUxMTA2NTc1NLWwsDQ2Mzcw38DI-IpRtsonsbwytUhBIyo1J1UhOD-ntCQzP69YU8GxLLN4ESt-eQBYODvsWgAAAA&rldimm=2544035751588936707&tbm=lcl&hl=fr-FR&ved=2ahUKEwi87uDh4_STAxVlnycCHWDyFeYQ9fQKegQIUBAG&biw=1708&bih=1064&dpr=2#lkt=LocalPoiReviews',
};

// ─────────────────────────────────────────────────────────────────────────────

export default function Hero() {
  const refs = {
    hero: useRef<HTMLElement>(null),
    tag: useRef<HTMLDivElement>(null),
    title: useRef<HTMLHeadingElement>(null),
    actions: useRef<HTMLDivElement>(null),
    trust: useRef<HTMLAnchorElement>(null),
    image: useRef<HTMLDivElement>(null),
  };

  useGSAP(
    () => {
      if (!refs.hero.current) return;

      const splitTitle = new SplitText(refs.title.current, { type: 'lines', mask: 'lines' });

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 14 });
      gsap.set(splitTitle.lines, { yPercent: MOTION.yPercent });
      gsap.set(refs.actions.current, { autoAlpha: 0, y: 10 });
      gsap.set(refs.trust.current, { autoAlpha: 0, y: 8 });
      gsap.set(refs.image.current, { clipPath: 'inset(100% 0 0 0)' });

      gsap
        .timeline({ defaults: { duration: MOTION.durationMd, ease: MOTION.ease } })
        .to(refs.tag.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm })
        .to(splitTitle.lines, { yPercent: 0, stagger: MOTION.staggerLines }, '-=0.1')
        .to(refs.actions.current, { autoAlpha: 1, y: 0 }, '<+0.2')
        .to(refs.trust.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm }, '<+0.15')
        .to(
          refs.image.current,
          { clipPath: 'inset(0% 0 0 0)', duration: MOTION.durationLg, ease: 'expo.out' },
          '<-0.5',
        );

      const mm = gsap.matchMedia();
      mm.add(
        {
          isDesktop: '(min-width: 1024px)',
          noMotion: '(prefers-reduced-motion: no-preference)',
        },
        (context) => {
          const { isDesktop, noMotion } = context.conditions as {
            isDesktop: boolean;
            noMotion: boolean;
          };
          if (!isDesktop || !noMotion) return;

          gsap.to(refs.image.current, {
            yPercent: -8,
            ease: 'none',
            scrollTrigger: {
              trigger: refs.hero.current,
              start: 'top top',
              end: 'bottom top',
              scrub: 1.5,
            },
          });
        },
      );

      return () => splitTitle.revert();
    },
    { scope: refs.hero },
  );

  return (
    <section
      ref={refs.hero}
      className="max-w-custom max-width-blue-section mx-auto grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16"
    >
      {/* ─── Colonne texte ───────────────────────────────────────────────── */}
      <div className="flex flex-col items-start gap-6">
        <Tag ref={refs.tag}>Logiciel de gestion pour avocats</Tag>

        <h1 ref={refs.title} className="font-medium">
          <span className="text-orange">zLawyer</span>, le logiciel de gestion et facturation pour
          cabinet d&apos;avocats{' '}
          <span className="text-orange">simple, ergonomique, sans engagement&nbsp;!</span>
        </h1>

        <div ref={refs.actions}>
          <Button href="/contact/" variant="fill">
            Je souhaite une démonstration
          </Button>
        </div>

        {/* Proof of trust */}
        <a
          ref={refs.trust}
          className="group flex w-full items-center justify-between gap-3 transition-opacity duration-200 hover:opacity-70 sm:w-auto sm:justify-start"
          href={GOOGLE_DATA.mapsUrl}
          rel="noreferrer"
          target="_blank"
        >
          {/* Note */}
          <span className="flex items-center gap-3">
            <GoogleIcon />
            <span
              aria-label={`${Math.round(GOOGLE_DATA.score)} étoiles sur 5`}
              className="flex items-center gap-0.5"
              role="img"
            >
              {Array.from({ length: 5 }, (_, i) => (
                <StarIcon key={i} />
              ))}
            </span>
            <span className="text-dark-blue font-roboto-medium text-sm">
              {GOOGLE_DATA.score.toFixed(1).replace('.', ',')}
              <span className="text-dark-blue/30 font-roboto-regular"> / 5</span>
            </span>
          </span>

          {/* Avis */}
          <span className="flex items-center gap-3">
            <span className="bg-dark-blue/10 hidden h-4 w-px sm:block" />
            <span className="text-dark-blue/50 text-sm">{GOOGLE_DATA.count} avis Google</span>
            <ArrowIcon />
          </span>
        </a>
      </div>

      {/* ─── Colonne image ───────────────────────────────────────────────── */}
      <div ref={refs.image} className="w-full overflow-visible">
        <Image
          alt="illustration du logiciel zLawyer"
          className="h-auto w-full max-w-none"
          decoding="async"
          height={3971}
          // sizes="(min-width: 1024px) 70vw, 100vw"
          src={withBasePath('/images/hero/illustrationHero.webp')}
          width={6567}
          priority
        />
      </div>
      <p className="sr-only">
        Expérimentez le logiciel de facturation le plus intuitif et performant du marché&nbsp;!
      </p>
    </section>
  );
}
